import { ApiReducer, makeApiAction, makeApiReducer, RsaaTypes } from '../../core/redux/api/reduxStandardApiAction';
import { IAppState } from '../../core/redux/IAppState';
import { addError, addLoading, getLoading, removeLoadingAndError } from '../../core/redux/api/module';
import { hasTrippedBreaker, updateBreaker } from '../../core/redux/api/circuitBreaker/module';
import { ACTION_TYPE } from '../../core/redux/actionType';
import { apiGuestComments } from '../../core/redux/api/endpoints';
import { toServerLocalTime } from '../../language/time';
import { GuestComment } from './guestComment';
import { updateGuest } from '../module';
import { assoc } from '../../language/module';
import { getResource } from '../../language/resources/getResource';


const KEY_ACTION = ACTION_TYPE.ADD_GUEST_COMMENT_REQUEST;
const ACTIONS    = [
  ACTION_TYPE.ADD_GUEST_COMMENT_REQUEST,
  ACTION_TYPE.ADD_GUEST_COMMENT_RESPONSE,
  ACTION_TYPE.ADD_GUEST_COMMENT_ERROR,
];


/**
 * Makes an action that can be dispatched to add a comment to a guest for future agents to read.
 */
export const addGuestCommentAction = (guestId: string, subject: string, comment: string) => {
  const meta = {
    guestId:    guestId,
    subject:    subject,
    comment:    comment,
    updateDate: toServerLocalTime(new Date()),
  };

  return makeApiAction(
    {
      method:   'POST',
      headers:  (state: IAppState) => ({
        'content-type':  'application/json',
        'Authorization': `Bearer ${state.auth.bearerToken}`,
      }),
      endpoint: apiGuestComments(guestId),
      body:     state =>  JSON.stringify({
        userId:      state.userState.userId,
        subjectText: subject,
        operatorId:  state.userState.userName,
        commentText: comment,
        updateDate:  new Date(Date.now()).toISOString(),
        resortAbbrv: state.selectedResort.resortAbbreviation
      }),
      types:    ACTIONS.map(action => ({ type: action, meta: meta })) as RsaaTypes,
      bailout:  (state: IAppState) => !!getLoading(state, guestId, KEY_ACTION) || hasTrippedBreaker(state, KEY_ACTION),
    },
  );
};


const reducer: ApiReducer<undefined> = (state, action, error, isLoading, payload) => {
  const guestId    = action.meta.guestId;
  const newComment = {
    guestId:     guestId as string,
    operatorId:  state.auth.username,
    subjectText: action.meta.subject as string,
    commentText: action.meta.comment as string,
    updateDate:  action.meta.updateDate as string,
  } as GuestComment;


  if (isLoading) {
    return addLoading(state, guestId, KEY_ACTION);
  }

  const withBreaker = updateBreaker(state, KEY_ACTION, !!error);

  if (error) {
    return addError(withBreaker, guestId, KEY_ACTION, error || getResource(state.locale, 'ADD_COMMENT_ERROR'));
  }

  const noLoadingOrError = removeLoadingAndError(withBreaker, guestId, KEY_ACTION);

  const updatedComments = assoc(
    'commentHistories',
    [newComment].concat(noLoadingOrError.commentHistories.filter(comment => comment.guestId !== guestId)),
    noLoadingOrError,
  );

  return updateGuest(updatedComments, guestId, guest => assoc('hasComments', true, guest));
};


export const addGuestCommentReducer = makeApiReducer<undefined>(ACTIONS, KEY_ACTION, reducer);
